import React, { useState } from 'react';
import { format } from 'date-fns';
import { HiOutlineTrash } from 'react-icons/hi';
import useAdmin from '../../../hooks/useAdmin';
import './ReportDetailView.scss';

const ReportDetailView = ({ report, onClose, onStatusUpdate, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { isAdmin } = useAdmin();
  
  if (!report) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        // Firebase Timestamp object - convert seconds to milliseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        // Handle both seconds and milliseconds
        date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_testing: { color: 'warning', text: 'Pending Testing' },
      test_report_available: { color: 'info', text: 'Test Report Available' },
      assigned_to_developer: { color: 'primary', text: 'Assigned to Developer' },
      tested_and_resolved: { color: 'success', text: 'Tested and Resolved' }
    };

    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(report.id);
      onClose(); // Close modal after successful deletion
    } catch (error) {
      console.error('Error deleting report:', error);
      // Keep modal open on error so user can try again
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className="report-detail-modal">
      <div className="report-detail-header">
        <div className="header-content">
          <h3>Game Testing Report Details</h3>
          <div className="assignee-info">
            <div className="info-item">
              <span className="label">Status:</span>
              <span className="value">{getStatusBadge(report.status)}</span>
            </div>
            {report.assignedTo && (
              <div className="info-item">
                <span className="label">Assigned To:</span>
                <span className="value">{report.assignedTo.name || report.assignedTo.email}</span>
              </div>
            )}
          </div>
        </div>
        <div className="header-actions">
          {isAdmin && (
            <button 
              className="delete-button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              title="Delete Report"
            >
              <HiOutlineTrash />
              Delete
            </button>
          )}
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
      </div>
      
      <div className="report-detail-content">
        <div className="report-meta-section">
          <div className="meta-grid">
            <div className="meta-item">
              <span className="label">Week Range:</span>
              <span className="value">{report.weekRange || 'N/A'}</span>
            </div>
            <div className="meta-item">
              <span className="label">Created:</span>
              <span className="value">{formatDate(report.createdAt)}</span>
            </div>
            <div className="meta-item">
              <span className="label">Last Updated:</span>
              <span className="value">{formatDate(report.lastUpdated)}</span>
            </div>
          </div>
        </div>
        
        <div className="section">
          <h4>Games Tested</h4>
          {report.selectedGames && report.selectedGames.length > 0 ? (
            <ul className="games-list">
              {report.selectedGames.map((game, index) => (
                <li key={index}>{game}</li>
              ))}
            </ul>
          ) : (
            <p className="no-data">No games selected</p>
          )}
        </div>
        
        <div className="section">
          <h4>Testing Feedback</h4>
          <div className="feedback-grid">
            <div className="feedback-item">
              <h5>Overall Experience</h5>
              <p>{report.overallExperience || 'No feedback provided'}</p>
            </div>
            
            <div className="feedback-item">
              <h5>Levels Feedback</h5>
              <p>{report.levelsFeedback || 'No feedback provided'}</p>
            </div>
            
            <div className="feedback-item">
              <h5>Tutorial Feedback</h5>
              <p>{report.tutorialFeedback || 'No feedback provided'}</p>
            </div>
            
            <div className="feedback-item">
              <h5>Positive Aspects</h5>
              <p>{report.positiveAspects || 'No feedback provided'}</p>
            </div>
            
            <div className="feedback-item">
              <h5>Improvement Suggestions</h5>
              <p>{report.improvementSuggestions || 'No suggestions provided'}</p>
            </div>
          </div>
        </div>
        
        {report.bugReports && report.bugReports.length > 0 && (
          <div className="section">
            <h4>Bug Reports</h4>
            <div className="bug-reports-list">
              {report.bugReports.map((bug, index) => (
                <div key={index} className="bug-report-item">
                  <div className="bug-header">
                    <h5>{bug.title || `Bug #${index + 1}`}</h5>
                    {bug.status && (
                      <span className={`bug-status ${bug.status}`}>
                        {bug.status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <p className="bug-description">{bug.description}</p>
                  {bug.steps && (
                    <div className="bug-steps">
                      <h6>Steps to Reproduce:</h6>
                      <p>{bug.steps}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {report.images && report.images.length > 0 && (
          <div className="section">
            <h4>Attached Images ({report.images.length})</h4>
            <div className="image-gallery">
              {report.images.map((image, index) => (
                <div className="image-item" key={index}>
                  <a href={image.url} target="_blank" rel="noopener noreferrer">
                    <img 
                      src={image.url} 
                      alt={image.name || `Screenshot ${index + 1}`}
                      onError={(e) => {
                        console.error('Image failed to load:', image.url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </a>
                  <div className="image-name">{image.name || `Image ${index + 1}`}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="report-detail-footer">
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-confirm-header">
              <h3>Delete Report</h3>
            </div>
            <div className="delete-confirm-content">
              <p>Are you sure you want to delete this game testing report?</p>
              <p><strong>This action will permanently delete:</strong></p>
              <ul>
                <li>The testing report and all feedback</li>
                <li>All associated bug reports ({report.bugReports?.length || 0} reports)</li>
                <li>All uploaded images ({report.images?.length || 0} files)</li>
              </ul>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="delete-confirm-actions">
              <button 
                className="cancel-btn"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDetailView; 